import { useState, useEffect } from 'react';
import { HeroSection } from './components/HeroSection';
import { ContextSelect } from './components/ContextSelect';
import { ExperienceIntent } from './components/ExperienceIntent';
import { ExperienceQuestions } from './components/ExperienceQuestions';
import { Results } from './components/Results';
import { ThemeToggle } from './components/ThemeToggle';
import { SelfDiscoveryHub } from './components/SelfDiscoveryHub';
import { KinseyEstimator } from './components/kinsey/KinseyEstimator';
import { ConnectionDiscovery } from './components/connection/ConnectionDiscovery';
import { ConnectionReadiness } from './components/readiness/ConnectionReadiness';
import { AttractionAlignment } from './components/attraction/AttractionAlignment';
import { SharedView } from './components/SharedView';
import './App.css';

const SECTIONS = {
  HERO: 'hero',
  CONTEXT: 'context',
  INTENT: 'intent',
  QUESTIONS: 'questions',
  RESULTS: 'results'
};

const STORAGE_KEY = 'haevn-assessment-data';

function App() {
  const [currentTool, setCurrentTool] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('share') ? 'shared' : 'hub';
  });
  const [currentSection, setCurrentSection] = useState(SECTIONS.HERO);
  const [profiles, setProfiles] = useState([]);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  // Theme management
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Load saved data
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.profiles && data.profiles.length > 0) {
          const hasComplete = data.profiles.some(p => p.drivers);
          if (hasComplete) {
            setProfiles(data.profiles);
            setShowResumePrompt(true);
          }
        }
      } catch (e) {
        // ignore
      }
    }
  }, []);

  // Save data on change
  useEffect(() => {
    if (profiles.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        profiles,
        savedAt: new Date().toISOString()
      }));
    }
  }, [profiles]);

  const goTo = (section) => {
    setCurrentSection(section);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- FLOW HANDLERS ---

  const handleStart = () => {
    if (showResumePrompt) return; // handled by resume prompt
    setProfiles([]);
    setCurrentProfileIndex(0);
    goTo(SECTIONS.CONTEXT);
  };

  const handleResume = () => {
    setShowResumePrompt(false);
    goTo(SECTIONS.RESULTS);
  };

  const handleStartFresh = () => {
    localStorage.removeItem(STORAGE_KEY);
    setProfiles([]);
    setCurrentProfileIndex(0);
    setShowResumePrompt(false);
    goTo(SECTIONS.CONTEXT);
  };

  const handleContextSelect = ({ context, label }) => {
    const newProfile = {
      id: context + '-' + Date.now(),
      context,
      label,
      intent: [],
      mode: 'current',
      availability: 5,
      drivers: null
    };
    setProfiles(prev => {
      const updated = [...prev];
      updated[currentProfileIndex] = newProfile;
      return updated;
    });
    goTo(SECTIONS.INTENT);
  };

  const handleIntentSelect = (intentArr) => {
    setProfiles(prev => {
      const updated = [...prev];
      updated[currentProfileIndex] = { ...updated[currentProfileIndex], intent: intentArr };
      return updated;
    });
    goTo(SECTIONS.QUESTIONS);
  };

  const handleAssessmentComplete = (drivers) => {
    setProfiles(prev => {
      const updated = [...prev];
      updated[currentProfileIndex] = { ...updated[currentProfileIndex], drivers };
      return updated;
    });

    const nextIncomplete = profiles.findIndex((p, i) => i > currentProfileIndex && !p.drivers);
    if (nextIncomplete !== -1) {
      setCurrentProfileIndex(nextIncomplete);
      goTo(SECTIONS.CONTEXT);
    } else {
      goTo(SECTIONS.RESULTS);
    }
  };

  const handleAddProfile = () => {
    const newIndex = profiles.length;
    setProfiles(prev => [...prev, {
      id: 'new-' + Date.now(),
      context: null,
      label: '',
      intent: [],
      mode: 'current',
      availability: 5,
      drivers: null
    }]);
    setCurrentProfileIndex(newIndex);
    goTo(SECTIONS.CONTEXT);
  };

  const handleRetake = () => {
    localStorage.removeItem(STORAGE_KEY);
    setProfiles([]);
    setCurrentProfileIndex(0);
    goTo(SECTIONS.CONTEXT);
  };

  const handleEdit = () => {
    setCurrentProfileIndex(0);
    goTo(SECTIONS.CONTEXT);
  };

  const currentProfile = profiles[currentProfileIndex];

  return (
    <div className="app-container bg-background min-h-screen">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => {
              setCurrentTool('hub');
              setCurrentSection(SECTIONS.HERO);
              setShowResumePrompt(false);
            }}
            className="hover:opacity-80 transition-opacity"
            data-testid="home-link"
          >
            <img
              src="/haevn-logo.png"
              alt="HAEVN"
              className="h-6"
            />
          </button>
          <ThemeToggle theme={theme} onToggle={() => setTheme(t => t === 'light' ? 'dark' : 'light')} />
        </div>
      </header>

      {/* Tool Routing */}
      {currentTool === 'shared' && (
        <SharedView onGoHome={() => {
          window.history.replaceState({}, '', window.location.pathname);
          setCurrentTool('hub');
        }} />
      )}

      {currentTool === 'hub' && (
        <SelfDiscoveryHub onSelectTool={(tool) => {
          setCurrentTool(tool);
          if (tool === 'haevn') {
            // Check for saved HAEVN data
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
              try {
                const data = JSON.parse(saved);
                if (data.profiles?.some(p => p.drivers)) {
                  setProfiles(data.profiles);
                  setShowResumePrompt(true);
                  setCurrentSection(SECTIONS.HERO);
                  return;
                }
              } catch (e) { /* ignore */ }
            }
            setCurrentSection(SECTIONS.HERO);
          }
        }} />
      )}

      {currentTool === 'kinsey' && (
        <KinseyEstimator
          onBackToHub={() => setCurrentTool('hub')}
          onGoToHAEVN={() => {
            setCurrentTool('haevn');
            setCurrentSection(SECTIONS.HERO);
          }}
        />
      )}

      {currentTool === 'connection' && (
        <ConnectionDiscovery
          onBackToHub={() => setCurrentTool('hub')}
          onGoToHAEVN={() => {
            setCurrentTool('haevn');
            setCurrentSection(SECTIONS.HERO);
          }}
        />
      )}

      {currentTool === 'readiness' && (
        <ConnectionReadiness
          onBackToHub={() => setCurrentTool('hub')}
          onGoToHAEVN={() => {
            setCurrentTool('haevn');
            setCurrentSection(SECTIONS.HERO);
          }}
        />
      )}

      {currentTool === 'attraction-alignment' && (
        <AttractionAlignment
          onBackToHub={() => setCurrentTool('hub')}
          onGoToHAEVN={() => {
            setCurrentTool('haevn');
            setCurrentSection(SECTIONS.HERO);
          }}
        />
      )}

      {currentTool === 'haevn' && (
        <>
          {/* Resume Prompt */}
          {showResumePrompt && currentSection === SECTIONS.HERO && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/80 backdrop-blur-sm px-6">
              <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full shadow-lg">
                <h3 className="text-xl font-light text-foreground mb-2">Welcome back</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  You have a saved assessment with {profiles.length} {profiles.length === 1 ? 'profile' : 'profiles'}.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleResume}
                    className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
                    data-testid="resume-btn"
                  >
                    View Results
                  </button>
                  <button
                    onClick={handleStartFresh}
                    className="flex-1 px-4 py-3 border border-border rounded-full text-sm font-medium text-foreground hover:bg-secondary transition-colors"
                    data-testid="start-fresh-btn"
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* HAEVN Sections */}
          {currentSection === SECTIONS.HERO && (
            <HeroSection onStart={showResumePrompt ? () => {} : handleStart} />
          )}

          {currentSection === SECTIONS.CONTEXT && (
            <ContextSelect
              onSelect={handleContextSelect}
              onBack={() => goTo(SECTIONS.HERO)}
            />
          )}

          {currentSection === SECTIONS.INTENT && currentProfile && (
            <ExperienceIntent
              onSelect={handleIntentSelect}
              onBack={() => goTo(SECTIONS.CONTEXT)}
              contextLabel={currentProfile.label}
            />
          )}

          {currentSection === SECTIONS.QUESTIONS && currentProfile && (
            <ExperienceQuestions
              onComplete={handleAssessmentComplete}
              onBack={() => goTo(SECTIONS.INTENT)}
              contextLabel={currentProfile.label}
              intentLabels={currentProfile.intent}
            />
          )}

          {currentSection === SECTIONS.RESULTS && (
            <Results
              profiles={profiles.filter(p => p.drivers)}
              onEdit={handleEdit}
              onRetake={handleRetake}
              onAddProfile={handleAddProfile}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;
